// platform/capabilities.js
//
// What this device can do: the ONE reading of "inside Telegram?", "installed?",
// "Apple?", "real iOS Safari?", "in-app webview?" and which app's, "Huawei?",
// "Android?", "an Android browser other than Chrome?", and the two derived answers the
// install and push flows branch on (`installChannel()`, `pushCapability()`).
//
// Before this module, appSurface(), usePwaInstall, IosInstallCoach,
// HuaweiBatteryCoach, the push gate and launchTarget each re-derived these on
// their own and agreed by luck (PWA install/notifications audit 2026-11-03,
// Section 3.1).
//
// The LOWEST layer of src/platform/: pure, no Vue, no Pinia, no Framework7, no
// store, no imports at all, so main.js can read it before Vue boots and
// platform/index.js can re-export from it without a cycle. Every function
// re-evaluates on each call (the Telegram globals can land after module init).
// The device checks read their globals through `read()`, so a throwing
// matchMedia or navigator getter yields the safe value instead of breaking
// boot; `isTelegramEnv` is moved here byte for byte and reads them directly,
// as it always did.

/** Run a global getter; a throw returns the fallback. */
function read(getter, fallback) {
  try {
    return getter();
  } catch {
    return fallback;
  }
}

const userAgent = () =>
  typeof navigator === 'undefined' ? '' : read(() => navigator.userAgent || '', '');

/**
 * Comprehensive Telegram-environment detection.
 *
 * Single source of truth for "are we running inside the Telegram webview?".
 * Pure function — only inspects globals, no network, no storage, no Vue. Safe
 * to call from pre-Vue boot code (main.js) and from any module that needs the
 * platform branch. Re-exported unchanged by platform/index.js, whose `isTma`
 * is a live alias of it.
 *
 * Signals (any truthy → TMA):
 *  - window.Telegram.WebApp.initData       — Telegram Mini Apps SDK injection
 *  - window.TelegramWebviewProxy*          — desktop / older webview bridges
 *  - window.TelegramGameProxy*             — legacy game-mode bridge
 *  - window.telegramEnv                    — bootstrap flag set by src/index.html
 *                                            (covers ordering races where one of
 *                                            the above isn't injected before the
 *                                            module evaluates)
 *  - navigator.userAgent matches /Telegram(Desktop|iOS|Android)/
 *                                          — UA fallback for webviews that don't
 *                                            inject the bridge globals early.
 *                                            Narrowed from a loose 'Telegram'
 *                                            substring to avoid false positives
 *                                            from third-party UAs containing the
 *                                            word (PWA audit MEDIUM, 2026-05-17).
 *  - document.referrer.startsWith('https://t.me/')
 *                                          — direct deep-link entry. Narrowed
 *                                            from .includes('t.me') so that a
 *                                            PWA user arriving from any URL that
 *                                            happens to contain 't.me' (e.g. a
 *                                            blog post linking to a Telegram
 *                                            channel) is not misclassified as
 *                                            TMA. Only true Telegram-origin
 *                                            referrers match.
 *
 * Rationale: previously platform/index.js used 2 signals while serviceWorker-
 * Cleanup used 7 and main.js used 3, creating a window where a Telegram session
 * could be classified `pwa` here (triggering SW registration in PwaAdapter)
 * while cleanup correctly fired in main.js. Consolidating fixes that drift.
 * PWA playbook Section 1 invariant; PWA audit MEDIUM-8.
 */
const TELEGRAM_UA_RE = /Telegram(Desktop|iOS|Android)/;

export function isTelegramEnv() {
  if (typeof window === 'undefined') return false;
  return Boolean(
    window.Telegram?.WebApp?.initData ||
      window.TelegramWebviewProxy ||
      window.TelegramWebviewProxyProto ||
      window.TelegramGameProxy ||
      window.TelegramGameProxy_receiveEvent ||
      window.telegramEnv ||
      (typeof navigator !== 'undefined' && TELEGRAM_UA_RE.test(navigator.userAgent)) ||
      (typeof document !== 'undefined' && document.referrer.startsWith('https://t.me/')),
  );
}

/**
 * Running as an installed app? `display-mode: standalone` (Android, desktop,
 * iOS 16.4+) or Apple's legacy `navigator.standalone` (iOS Home Screen).
 */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    read(() => window.matchMedia?.('(display-mode: standalone)').matches === true, false) ||
    read(() => window.navigator.standalone === true, false)
  );
}

/**
 * An Apple touch device (iPhone / iPad / iPod), where install-for-push applies.
 * iPadOS 13+ masquerades as desktop Safari (platform 'MacIntel'), so MacIntel
 * with a touch screen counts too.
 */
export function isAppleDevice() {
  if (typeof navigator === 'undefined') return false;
  if (/iPad|iPhone|iPod/.test(userAgent())) return true;
  return read(() => navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1, false);
}

/**
 * Real iOS Safari: the ONLY iOS browser that can install a PWA and, once
 * installed, grant push. False for Chrome / Firefox / Edge / Opera / Yandex /
 * DuckDuckGo on iOS (they carry their own engine token) and for in-app
 * WKWebviews (FBAN / Instagram / Line), which drop "Version/" or "Safari".
 */
export function isIosSafari() {
  if (!isAppleDevice()) return false;
  const ua = userAgent();
  if (/CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|DuckDuckGo/i.test(ua)) return false;
  return /Safari/.test(ua) && /Version\//.test(ua);
}

/**
 * Android, `navigator.userAgentData` first (Chromium), the UA string as the
 * fallback. Cheap, not fingerprint-grade: launchTarget's "Open in Chrome"
 * affordance has `browser_fallback_url` for the false negative.
 */
export function isAndroid() {
  if (typeof navigator === 'undefined') return false;
  const uad = read(() => navigator.userAgentData, undefined);
  if (uad?.platform) return /android/i.test(uad.platform);
  return /Android/i.test(userAgent());
}

/**
 * An Android browser that is not Chrome and cannot install the app for real:
 * Samsung Internet (its WebAPK installs have shown Play Protect's "blocked
 * dangerous app" on Android 14+ since April 2026), Mi / HyperOS, Oppo, Realme
 * and vivo (HeyTap), Phoenix (TECNO / Infinix), Opera and Opera Mini, Firefox
 * and UC, which make a browser-badged shortcut at best and fire no
 * beforeinstallprompt worth waiting for; Edge on Android (EdgA) makes a
 * shortcut too. There the install is a handoff to Chrome with a launch token
 * (usePwaInstall's "install in Chrome" sheet). Brave carries Chrome's own
 * tokens and is not told apart. UA string only: userAgentData hides the brand
 * (owner research 2026-11-03). Some of these browsers (HeyTap, vivo) build on
 * the system WebView and say `; wv)`: they are browsers, not in-app views.
 */
const ANDROID_NON_CHROME_RE =
  /SamsungBrowser\/|MiuiBrowser|HeyTapBrowser|OppoBrowser|RealmeBrowser|VivoBrowser|PHX\/|OPR\/|OPT\/|Opera Mini|Firefox\/|UCBrowser|EdgA\//;

// The in-app browser views that keep a sign-in to themselves and cannot
// install: Facebook and Messenger (FBAN / FBAV / FB_IAB; Messenger says
// Orca-Android or MessengerForiOS), Instagram, TikTok (musical_ly,
// BytedanceWebview), WhatsApp, Line.
const IN_APP_HOST_RE = /FBAN|FBAV|FB_IAB|Instagram|musical_ly|BytedanceWebview|TikTok|WhatsApp|Line\//;
const MESSENGER_RE = /Orca-Android|MessengerForiOS|MessengerLiteForiOS|FB_IAB\/MESSENGER/;
// A real iOS browser that is not Safari: a browser, not an in-app view.
const IOS_OTHER_BROWSER_RE = /CriOS|FxiOS|EdgiOS|OPiOS|YaBrowser|DuckDuckGo/i;

/**
 * Inside another app's browser view, where installing and push are out of
 * reach: on Apple, anything that is not Safari and not already installed
 * (in-app WKWebviews, and the Chrome / Firefox iOS shells); on Android, the
 * system WebView (`; wv)`) or a known social-app view (Facebook, Messenger,
 * Instagram, TikTok, WhatsApp, Line).
 */
export function isInAppWebview() {
  if (isAppleDevice()) return !isIosSafari() && !isStandalone();
  if (!isAndroid()) return false;
  const ua = userAgent();
  if (IN_APP_HOST_RE.test(ua) || MESSENGER_RE.test(ua)) return true;
  // A real browser built on the WebView (HeyTap, vivo) is not an in-app view.
  if (ANDROID_NON_CHROME_RE.test(ua)) return false;
  return /; wv\)/.test(ua);
}

/**
 * Which app's browser view this page is open in — the escape banner names it
 * (pack prompt 7 Part C): 'facebook' | 'messenger' | 'instagram' | 'tiktok'
 * | 'whatsapp' | 'line' | 'webview' (an in-app view with no known host: the
 * Android system WebView, an iOS WKWebView) | null (a real browser, an
 * installed app). The Telegram Mini App wins: it is never an in-app browser
 * here. A real iOS browser other than Safari (Chrome, Firefox, Edge, …) is a
 * browser too — a sign-in there keeps.
 *
 * @returns {'facebook'|'messenger'|'instagram'|'tiktok'|'whatsapp'|'line'|'webview'|null}
 */
export function inAppKind() {
  if (isTelegramEnv()) return null;
  if (isStandalone()) return null;
  const ua = userAgent();
  if (MESSENGER_RE.test(ua)) return 'messenger';
  if (/FBAN|FBAV|FB_IAB/.test(ua)) return 'facebook';
  if (/Instagram/.test(ua)) return 'instagram';
  if (/musical_ly|BytedanceWebview|TikTok/.test(ua)) return 'tiktok';
  if (/WhatsApp/.test(ua)) return 'whatsapp';
  if (/Line\//.test(ua)) return 'line';
  if (isAndroid()) {
    if (ANDROID_NON_CHROME_RE.test(ua)) return null;
    return /; wv\)/.test(ua) ? 'webview' : null;
  }
  if (isAppleDevice() && !isIosSafari() && !IOS_OTHER_BROWSER_RE.test(ua)) return 'webview';
  return null;
}

export function isAndroidNonChrome() {
  if (!isAndroid()) return false;
  return ANDROID_NON_CHROME_RE.test(userAgent());
}

/**
 * Heuristic: is this likely a Huawei Android device that ships without
 * Google Play Services (or where EMUI/HarmonyOS aggressive battery management
 * is the default)?
 *
 * UA tokens checked (case-insensitive):
 *   - HUAWEI / HONOR — brand markers (Honor split off in 2020 but still ships
 *     HMS-only devices for the same userbase)
 *   - EMUI / HarmonyOS — OS markers
 *   - HMSCore — Huawei Mobile Services presence indicator
 *
 * Android gate first so iOS/Windows/macOS Safari UAs that happen to contain
 * one of the tokens (none do today, but defensive) are filtered. Reads the UA
 * string only, never userAgentData (HuaweiBatteryCoach's heuristic, verbatim).
 */
export function isHuaweiAndroid() {
  const ua = userAgent();
  // Bail outside Android — the EMUI/HarmonyOS battery quirks are Android-only.
  if (!/android/i.test(ua)) return false;
  return /HUAWEI|HONOR|EMUI|HarmonyOS|HMSCore/i.test(ua);
}

/**
 * How this device installs the app, evaluated in this order:
 *  - 'tma'            inside Telegram: no install, no worker.
 *  - 'installed'      already running standalone.
 *  - 'ios-safari'     Apple + real Safari: the Add to Home Screen steps.
 *  - 'ios-webview'    Apple + anything else: open in Safari first.
 *  - 'android-prompt' Android, not installed: the install card. An Android
 *                     in-app webview reports this too (today's install card
 *                     shows there); a caller that needs Chrome itself adds
 *                     `!isInAppWebview()`.
 *  - 'desktop'        everything else.
 *
 * @returns {'tma'|'installed'|'ios-safari'|'ios-webview'|'android-prompt'|'desktop'}
 */
export function installChannel() {
  if (isTelegramEnv()) return 'tma';
  if (isStandalone()) return 'installed';
  if (isAppleDevice()) return isIosSafari() ? 'ios-safari' : 'ios-webview';
  if (isAndroid()) return 'android-prompt';
  return 'desktop';
}

/**
 * What web push can do here, evaluated in this order:
 *  - 'tma'           inside Telegram: Telegram delivers, never web push.
 *  - 'needs-install' Apple and not standalone: iOS / iPadOS web push (16.4+)
 *                    exists only inside an installed app. Checked BEFORE the
 *                    API test because a Safari tab does not expose PushManager
 *                    or Notification at all; testing them first would report
 *                    every iPhone tab as 'unsupported'.
 *  - 'unsupported'   no serviceWorker, PushManager or Notification (this
 *                    includes an installed app on iOS older than 16.4).
 *  - 'denied' | 'granted' | 'default'  the native `Notification.permission`.
 *
 * @returns {'tma'|'unsupported'|'needs-install'|'denied'|'granted'|'default'}
 */
export function pushCapability() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return 'unsupported';
  if (isTelegramEnv()) return 'tma';
  if (isAppleDevice() && !isStandalone()) return 'needs-install';
  const supported = read(
    () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window,
    false,
  );
  if (!supported) return 'unsupported';
  const permission = read(() => window.Notification.permission, 'default');
  if (permission === 'denied' || permission === 'granted') return permission;
  return 'default';
}
